<template>
  <div
    v-if="isLivePreviewVisible"
    class="shopActionBar"
    :class="{ fullscreen: fullScreen }"
  >
    <div v-if="fullScreen" class="toolbar">
      <div class="mode-wrapper">
        <button
          class="btn btn-small"
          :class="{ selected: mode === 'desktop' }"
          @click="changeMode('desktop')"
        >
          <Icon icon="desktop"></Icon>
        </button>
        <button
          class="btn btn-small"
          :class="{ selected: mode === 'tablet' }"
          @click="changeMode('tablet')"
        >
          <Icon icon="tablet"></Icon>
        </button>
        <button
          class="btn btn-small"
          :class="{ selected: mode === 'mobile' }"
          @click="changeMode('mobile')"
        >
          <Icon icon="phone-case"></Icon>
        </button>
      </div>
      <a
        class="link-main icon-link collapse-preview-link"
        @click="closePreview"
      >
        <Icon icon="fullscreen-off"></Icon>
        {{ $t('SA.action.collapsePreview') }}
      </a>
    </div>
    <div v-else class="toolbar">
      <a class="link-main icon-link" @click="openPreview">
        <Icon icon="fullscreen-on"></Icon>
        {{ $t('SA.action.expandPreview') }}
      </a>
      <a :href="shopUrl" target="_blank" class="link-main icon-link">
        <Icon icon="globe_light"></Icon>
        {{ $t('SA.menubar.preview') }}
      </a>
      <!--
        Complementary on `newStorefront`, so exactly one of the two can ever show. Gating the legacy
        one on the partner-wide sample order condition instead left legacy shops of a publishing-core
        partner with neither.
      -->
      <a
        v-if="sampleOrderLinkVisible"
        class="link-main icon-link"
        @click="orderSamples"
      >
        <Icon icon="shipping-box" />
        {{ $t('SAMPLE_ORDER.ENTRY_POINT') }}
      </a>
      <a
        v-if="!newStorefront"
        class="link-main icon-link"
        @click="openSelfOrderModal"
      >
        <Icon icon="basket" />
        {{
          $t('POS.SHOP_SETTINGS.NAVIGATION.SELFORDER_DIALOG.OPEN_BTN_CAPTION')
        }}
      </a>
    </div>
  </div>
</template>

<script>
import dialogService from 'commons/dialogs/wrapper/dialogService';
import ShopSelfOrderDialog from 'src/app/partnerarea/pos/shop/navigation/ShopSelfOrderDialog.vue';
import { canStartShopSampleOrder } from 'commons/sampleOrder/sampleOrderChannels';
import { openSampleOrderEntry } from 'commons/sampleOrder/sampleOrderEntry';
import {
  SAMPLE_ORDER_SOURCE,
  trackSampleOrderEntryClick,
} from 'commons/sampleOrder/sampleOrderTracking';
import { PUBLISHING_SCOPE } from 'commons/publishing/publishingScope';

export default {
  name: 'ShopActionBar',
  computed: {
    isLivePreviewVisible() {
      return this.$store.state.shop.livePreview.visible;
    },
    fullScreen() {
      return this.$store.state.shop.livePreview.fullScreen;
    },
    mode() {
      return this.$store.state.shop.livePreview.mode;
    },
    shopUrl() {
      return this.$store.getters['shop/getSpreadshopUrl']('', false);
    },
    newStorefront() {
      return this.$store.state.shop.currentShop.newStorefront;
    },
    /**
     * `isShopEmpty` keeps a link out of a shop with nothing to sample. It is the flag the rest of Shop
     * Settings reads for that, so this reports "empty" for the same shops as `NoProductsPlaceholder` —
     * including one holding only unpublished compositions.
     */
    sampleOrderLinkVisible() {
      return (
        canStartShopSampleOrder() &&
        this.newStorefront &&
        !this.$store.getters['shop/isShopEmpty']
      );
    },
  },
  methods: {
    openPreview() {
      this.$store.commit('shop/setLivePreviewFullScreen', true);
    },
    closePreview() {
      this.$store.commit('shop/setLivePreviewFullScreen', false);
      this.$store.commit('shop/setLivePreviewMode', 'desktop');
    },
    changeMode(newMode) {
      this.$store.commit('shop/setLivePreviewMode', newMode);
    },
    openSelfOrderModal() {
      dialogService.openDialog(ShopSelfOrderDialog, null, { fullSize: true });
    },
    orderSamples() {
      trackSampleOrderEntryClick({
        source: SAMPLE_ORDER_SOURCE.SHOP_SETTINGS,
        scope: PUBLISHING_SCOPE.SHOP.key,
      });

      openSampleOrderEntry({
        shopId: this.$store.state.shop.currentShop.id,
        source: SAMPLE_ORDER_SOURCE.SHOP_SETTINGS,
      }).catch(() => {
        // Backing out of the dialog rejects; nothing to do about it here.
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import 'src/scss/styleguide/colors';
.shopActionBar {
  width: 100%;
  background-color: #fff;
  padding: 16px;

  .mode-wrapper {
    display: flex;
    justify-content: center;

    .btn {
      border: none;
      padding: 0 24px;

      .icon {
        width: 32px;
        height: 32px;
      }

      &.selected {
        color: $pa-color-main;
      }
    }
  }

  a {
    padding: 0 16px;

    &:not(:last-child) {
      border-right: 1px solid $grey20;
    }
  }

  .collapse-preview-link {
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    display: flex;
    align-items: center;
  }

  .toolbar {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
  }
}
</style>
